// Hotel Staff Hub — operational screens (Phase C).
import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Clock, DoorClosed, LayoutGrid, Camera, X } from 'lucide-react';
import { RequestThread } from '../commandCenter/RequestThread';

const fmtTime = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
};

const Shell = ({ title, tx, onBack, onRefresh, children, testId }) => {
    const [refreshing, setRefreshing] = useState(false);
    const doRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try { await onRefresh(); } catch { /* ignore */ }
        finally { setTimeout(() => setRefreshing(false), 400); }
    };
    return (
        <div className="w-full max-w-lg mx-auto" data-testid={testId}>
            <div className="flex items-center justify-between mb-4">
                <button type="button" onClick={onBack}
                    className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white transition-colors"
                    data-testid="staff-back-btn">
                    <ChevronLeft className="w-4 h-4" /> {tx.back}
                </button>
                {onRefresh && (
                    <button type="button" onClick={doRefresh} disabled={refreshing}
                        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors disabled:opacity-60"
                        data-testid="staff-refresh-btn">
                        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> {tx.refresh}
                    </button>
                )}
            </div>
            <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h2>
            {children}
        </div>
    );
};

export const HousekeepingView = ({ tx, call, onBack, onOpenRoom, onOpenRequests }) => {
    const [rooms, setRooms] = useState(null);
    const [busyRoom, setBusyRoom] = useState('');
    const [reqCount, setReqCount] = useState(null);

    const load = useCallback(async () => {
        try { const d = await call('housekeeping/rooms'); setRooms(d.rooms || []); }
        catch { /* locked handled upstream */ }
        // Requests ROUTED to housekeeping (additive — the rooms view is unchanged).
        try {
            const q = await call('requests/housekeeping?status=open');
            setReqCount((q.requests || []).length);
        } catch { setReqCount(null); }
    }, [call]);
    useEffect(() => { load(); }, [load]);

    const markReady = async (rn) => {
        setBusyRoom(rn);
        try { await call(`housekeeping/rooms/${encodeURIComponent(rn)}/ready`, { method: 'POST' }); await load(); }
        catch { /* locked handled upstream */ }
        finally { setBusyRoom(''); }
    };

    if (!rooms) return <Shell title={tx.housekeeping} tx={tx} onBack={onBack} testId="staff-housekeeping-view"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin mx-auto" /></Shell>;

    const floors = {};
    rooms.forEach((r) => { (floors[r.floor] = floors[r.floor] || []).push(r); });

    return (
        <Shell title={tx.housekeeping} tx={tx} onBack={onBack} onRefresh={load} testId="staff-housekeeping-view">
            {onOpenRequests && (
                <button type="button" onClick={onOpenRequests}
                    className="w-full flex items-center justify-between gap-3 mb-4 rounded-xl bg-white/[0.05] border border-white/10 p-3 text-left transition-colors hover:bg-white/[0.09]"
                    data-testid="staff-hk-requests-open">
                    <span className="min-w-0">
                        <span className="block text-white font-semibold text-sm">{tx.hkRequests}</span>
                        <span className="block text-slate-400 text-xs mt-0.5">{tx.hkRequestsHelp}</span>
                    </span>
                    <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${reqCount ? 'bg-emerald-600 text-white' : 'bg-white/10 text-slate-400'}`}
                        data-testid="staff-hk-requests-count">
                        {reqCount ? `${reqCount} ${tx.notifSuffix}` : tx.allClear}
                    </span>
                </button>
            )}
            {rooms.length === 0 && <p className="text-slate-400 text-sm">{tx.noRooms}</p>}
            {Object.keys(floors).sort().map((f) => (
                <div key={f} className="mb-5">
                    <p className="text-slate-400 text-xs uppercase tracking-widest mb-2">{tx.floor} {f}</p>
                    <div className="space-y-2">
                        {floors[f].map((r) => (
                            <div key={r.room_number}
                                className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3"
                                data-testid={`hk-room-${r.room_number}`}>
                                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${(r.dnd || r.room_status === 'servicing') ? 'bg-amber-500' : r.room_status === 'not_ready' ? 'bg-rose-500' : 'bg-emerald-500'}`} />
                                <button type="button" onClick={() => onOpenRoom && onOpenRoom(r.room_number)}
                                    className="flex-1 min-w-0 text-left" data-testid={`hk-room-open-${r.room_number}`}>
                                    <p className="text-white font-semibold text-sm">{r.room_number}</p>
                                    <p className="text-xs mt-0.5" data-testid={`hk-room-status-${r.room_number}`}>
                                        {r.room_status === 'servicing'
                                            ? <span className="text-amber-300">{tx.beingServiced}</span>
                                            : r.dnd
                                                ? <span className="text-amber-300">{tx.stateOrange} · {r.dnd_until || '—'}</span>
                                                : r.room_status === 'not_ready'
                                                    ? <span className="text-rose-300">{tx.stateRed}</span>
                                                    : <span className="text-emerald-300">{tx.ready}</span>}
                                    </p>
                                </button>
                                {(r.dnd || r.room_status === 'servicing' || r.room_status === 'not_ready') && (
                                    <button type="button" onClick={() => markReady(r.room_number)} disabled={busyRoom === r.room_number}
                                        className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors disabled:opacity-60"
                                        data-testid={`hk-mark-ready-${r.room_number}`}>
                                        {busyRoom === r.room_number ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : tx.markReady}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </Shell>
    );
};

export const FloorView = ({ tx, call, session, onBack, onOpenTools, onUpdateRoom, onOpenRoom }) => {
    const [data, setData] = useState(null);
    const load = useCallback(async () => {
        try { const d = await call('floor/rooms'); setData(d); } catch { /* locked upstream */ }
    }, [call]);
    useEffect(() => { load(); }, [load]);

    const floor = session?.floor;
    const counts = data?.counts || { red: 0, orange: 0, green: 0, total: 0 };
    const doorColor = { red: 'text-rose-400', orange: 'text-amber-400', green: 'text-emerald-400' };
    const dotColor = { red: 'bg-rose-500', orange: 'bg-amber-500', green: 'bg-emerald-500' };

    const Stat = ({ state, label, testId }) => (
        <div className="flex flex-col items-center rounded-xl bg-white/5 border border-white/10 py-2.5" data-testid={testId}>
            <span className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-full ${dotColor[state]}`} />
                <span className="text-white font-bold text-lg">{counts[state]}</span>
            </span>
            <span className="text-[11px] text-slate-400 mt-0.5 text-center leading-tight">{label}</span>
        </div>
    );

    return (
        <div className="w-full max-w-lg mx-auto" data-testid="staff-floor-view">
            <div className="flex items-center justify-between mb-3">
                <button type="button" onClick={onBack}
                    className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white transition-colors"
                    data-testid="staff-back-btn">
                    <ChevronLeft className="w-4 h-4" /> {tx.back}
                </button>
                <button type="button" onClick={load}
                    className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors"
                    data-testid="staff-refresh-btn">
                    <RefreshCw className="w-3.5 h-3.5" /> {tx.refresh}
                </button>
            </div>

            {/* Arrival context — makes the physical tap feel meaningful */}
            <div className="text-center mb-5">
                <p className="text-slate-400 text-xs uppercase tracking-widest">{tx.arrivedFloor}</p>
                <p className="text-4xl font-bold text-white mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}
                    data-testid="staff-floor-heading">
                    {tx.floor} {floor}
                </p>
                <p className="text-slate-400 text-sm mt-1">{counts.total} {tx.rooms}</p>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-5">
                <Stat state="red" label={tx.stateRed} testId="floor-count-red" />
                <Stat state="orange" label={tx.stateOrange} testId="floor-count-orange" />
                <Stat state="green" label={tx.stateGreen} testId="floor-count-green" />
            </div>

            <p className="text-white font-semibold mb-3">{tx.floorRoomsTitle}</p>
            {onUpdateRoom && (
                <button type="button" onClick={onUpdateRoom}
                    className="w-full mb-4 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors inline-flex items-center justify-center gap-2"
                    data-testid="staff-update-room-btn">
                    <DoorClosed className="w-4 h-4" /> {tx.updateRoomStatus}
                </button>
            )}
            {!data ? (
                <Loader2 className="w-6 h-6 text-emerald-400 animate-spin mx-auto" />
            ) : counts.total === 0 ? (
                <p className="text-slate-400 text-sm">{tx.noFloorRooms}</p>
            ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="floor-door-grid">
                    {data.rooms.map((r) => (
                        <button key={r.room_number} type="button" data-state={r.state}
                            onClick={() => onOpenRoom && onOpenRoom(r.room_number)}
                            className="flex flex-col items-center gap-1 rounded-xl bg-white/5 border border-white/10 py-3 hover:bg-white/10 transition-colors"
                            data-testid={`floor-door-${r.room_number}`}>
                            <DoorClosed className={`w-7 h-7 ${doorColor[r.state]}`} strokeWidth={1.75} />
                            <span className="text-white text-sm font-semibold">{r.room_number}</span>
                        </button>
                    ))}
                </div>
            )}

            {onOpenTools && (
                <button type="button" onClick={onOpenTools}
                    className="mt-6 w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 text-slate-200 text-sm font-medium transition-colors inline-flex items-center justify-center gap-2"
                    data-testid="staff-floor-all-tools">
                    <LayoutGrid className="w-4 h-4" /> {tx.allTools}
                </button>
            )}
        </div>
    );
};


// Reusable staff-action photo control (P4). Holds selected File objects in
// `photos` (lifted to parent). Native camera on mobile; preview + remove.
export const StaffActionPhoto = ({ tx, photos, setPhotos, max = 3 }) => {
    const inputRef = useRef(null);
    const [urls, setUrls] = useState([]);
    // Create object URLs when the selection changes; revoke previous ones and
    // revoke all on unmount to avoid memory leaks during repeated capture.
    useEffect(() => {
        const next = photos.map((f) => URL.createObjectURL(f));
        setUrls(next);
        return () => next.forEach((u) => URL.revokeObjectURL(u));
    }, [photos]);
    const onPick = (e) => {
        const files = Array.from(e.target.files || []);
        setPhotos((prev) => [...prev, ...files].slice(0, max));
        e.target.value = '';
    };
    return (
        <div className="mt-4" data-testid="staff-action-photo">
            <p className="text-slate-300 text-sm mb-2">{tx.photoOptional}</p>
            <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple
                className="hidden" onChange={onPick} data-testid="staff-photo-input" />
            <div className="flex gap-2 flex-wrap">
                {photos.map((f, i) => (
                    <div key={i} className="relative">
                        <img src={urls[i]} alt="" className="w-16 h-16 rounded-lg object-cover border border-white/15"
                            data-testid={`staff-photo-preview-${i}`} />
                        <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                            className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-rose-600 text-white flex items-center justify-center"
                            data-testid={`staff-photo-remove-${i}`}>
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                ))}
                {photos.length < max && (
                    <button type="button" onClick={() => inputRef.current?.click()}
                        className="w-16 h-16 rounded-lg border border-dashed border-white/25 text-slate-300 hover:border-emerald-400 hover:text-white transition-colors flex flex-col items-center justify-center gap-1"
                        data-testid="staff-photo-add">
                        <Camera className="w-5 h-5" />
                        <span className="text-[10px] leading-tight">{tx.addPhoto}</span>
                    </button>
                )}
            </div>
        </div>
    );
};


export const RoomStatusView = ({ tx, callSafe, uploadActionPhotos, cleanupActionPhotos, session, onBack }) => {
    const [step, setStep] = useState('enter'); // 'enter' | 'room'
    const [roomInput, setRoomInput] = useState('');
    const [room, setRoom] = useState(null);
    const [reason, setReason] = useState('');
    const [photos, setPhotos] = useState([]);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState('');

    const floor = session?.floor;

    const errMsg = (detail) => ({
        room_not_found: tx.roomNotFound, room_wrong_floor: tx.roomWrongFloor,
        reason_required: tx.reasonRequired, invalid_transition: tx.invalidTransition,
    }[detail] || tx.error);

    const lookup = async (e) => {
        e?.preventDefault?.();
        const rn = roomInput.trim();
        if (!rn) return;
        setBusy(true); setError(''); setDone('');
        try {
            const { ok, data } = await callSafe(`floor/room/${encodeURIComponent(rn)}`);
            if (ok) { setRoom(data); setReason(''); setPhotos([]); setStep('room'); }
            else setError(errMsg(data?.detail));
        } catch { setError(tx.networkError); } finally { setBusy(false); }
    };

    const change = async (newState) => {
        if (newState === 'red' && !reason.trim()) { setError(tx.reasonRequired); return; }
        setBusy(true); setError('');
        let photo_ids;
        try {
            if (photos.length && uploadActionPhotos) {
                const up = await uploadActionPhotos(photos, { action_type: 'room_status', room_number: room.room_number });
                if (!up.ok) { setError(errMsg(up.data?.detail)); setBusy(false); return; }
                photo_ids = up.data.photo_ids || [];
            }
            const { ok, data } = await callSafe(`floor/room/${encodeURIComponent(room.room_number)}/status`,
                { method: 'POST', body: { new_state: newState, reason: reason.trim() || undefined, photo_ids } });
            if (ok) {
                setDone(`${tx.room} ${room.room_number} · ${tx.statusUpdated}`);
                setStep('enter'); setRoomInput(''); setRoom(null); setReason(''); setPhotos([]);
            } else {
                // Status change failed AFTER photos uploaded → clean up orphans.
                if (photo_ids?.length && cleanupActionPhotos) { cleanupActionPhotos(photo_ids); }
                setError(errMsg(data?.detail));
            }
        } catch {
            if (photo_ids?.length && cleanupActionPhotos) { cleanupActionPhotos(photo_ids); }
            setError(tx.networkError);
        } finally { setBusy(false); }
    };

    const stateColor = { red: 'text-rose-400', orange: 'text-amber-400', green: 'text-emerald-400' };
    const stateLabel = { red: tx.stateRed, orange: tx.stateOrange, green: tx.stateGreen };
    // one allowed transition per current state (LOCKED)
    const nextAction = room && {
        red: { to: 'orange', label: tx.startServicing, cls: 'bg-amber-600 hover:bg-amber-700' },
        orange: { to: 'green', label: tx.markRoomReady, cls: 'bg-emerald-600 hover:bg-emerald-700' },
        green: { to: 'red', label: tx.needsAttentionBtn, cls: 'bg-rose-600 hover:bg-rose-700' },
    }[room.state];

    return (
        <div className="w-full max-w-lg mx-auto" data-testid="staff-roomstatus-view">
            <div className="flex items-center mb-4">
                <button type="button" onClick={step === 'room' ? () => { setStep('enter'); setError(''); } : onBack}
                    className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white transition-colors"
                    data-testid="staff-back-btn">
                    <ChevronLeft className="w-4 h-4" /> {tx.back}
                </button>
            </div>

            {step === 'enter' && (
                <form onSubmit={lookup} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                    <p className="text-white text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {tx.hi}, {session?.staff_name} 👋
                    </p>
                    <p className="text-emerald-300 text-sm font-semibold mt-0.5">{tx.floor} {floor}</p>
                    <p className="text-slate-300 text-sm mt-4 mb-2">{tx.enterRoomTitle}</p>
                    <input inputMode="numeric" autoComplete="off" value={roomInput}
                        onChange={(e) => { setRoomInput(e.target.value); setError(''); }}
                        placeholder={tx.roomPlaceholder}
                        className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-2xl tracking-wider text-center py-3 px-4 placeholder:text-slate-600 focus:outline-none focus:border-emerald-500"
                        data-testid="roomstatus-input" />
                    {done && <p className="text-emerald-400 text-sm mt-3 text-center" data-testid="roomstatus-done">{done}</p>}
                    {error && <p className="text-rose-400 text-sm mt-3 text-center" data-testid="roomstatus-error">{error}</p>}
                    <button type="submit" disabled={!roomInput.trim() || busy}
                        className="mt-4 w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-50"
                        data-testid="roomstatus-continue">
                        {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : tx.continueBtn}
                    </button>
                </form>
            )}

            {step === 'room' && room && (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="roomstatus-room">
                    <div className="flex items-center gap-3">
                        <DoorClosed className={`w-9 h-9 ${stateColor[room.state]}`} strokeWidth={1.75} />
                        <div>
                            <p className="text-white text-xl font-bold">{tx.room} {room.room_number}</p>
                            <p className={`text-sm font-semibold ${stateColor[room.state]}`} data-testid="roomstatus-current">
                                {tx.currentStatus}: {stateLabel[room.state]}
                            </p>
                        </div>
                    </div>
                    {room.last_cleaned_at && (
                        <p className="text-xs text-slate-400 mt-2">
                            {tx.lastCleanedBy} {room.last_cleaned_by || '—'} · {fmtTime(room.last_cleaned_at)}
                        </p>
                    )}

                    {room.state === 'green' && (
                        <div className="mt-4">
                            <label className="text-slate-300 text-sm">{tx.reasonLabel}</label>
                            <input value={reason} onChange={(e) => { setReason(e.target.value); setError(''); }}
                                placeholder={tx.reasonPlaceholder}
                                className="mt-1 w-full rounded-xl bg-slate-900/60 border border-white/15 text-white py-2.5 px-3 placeholder:text-slate-600 focus:outline-none focus:border-rose-500"
                                data-testid="roomstatus-reason" />
                        </div>
                    )}
                    <StaffActionPhoto tx={tx} photos={photos} setPhotos={setPhotos} />
                    {error && <p className="text-rose-400 text-sm mt-3 text-center" data-testid="roomstatus-error">{error}</p>}
                    {nextAction && (
                        <button type="button" onClick={() => change(nextAction.to)} disabled={busy}
                            className={`mt-4 w-full py-3.5 rounded-xl text-white font-semibold transition-colors disabled:opacity-50 ${nextAction.cls}`}
                            data-testid="roomstatus-action">
                            {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : nextAction.label}
                        </button>
                    )}
                    <button type="button" onClick={() => { setStep('enter'); setRoomInput(''); setRoom(null); setError(''); }}
                        className="mt-3 w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-slate-200 text-sm font-medium transition-colors"
                        data-testid="roomstatus-another">
                        {tx.enterAnother}
                    </button>
                </div>
            )}
        </div>
    );
};


const catLabel = (tx, kind, c) => {
    const map = kind === 'security' ? tx.secCatStaff : tx.conCatStaff;
    return (map && map[c]) || (c || '').replace(/_/g, ' ');
};

const _durMin = (a, b) => {
    if (!a || !b) return null;
    try { const m = (new Date(b) - new Date(a)) / 60000; return m >= 0 ? Math.round(m) : null; }
    catch { return null; }
};

// Full operational record for a single request — the Manager (and any staff)
// drill-down. Built entirely from the EXISTING request doc fields.
export const RequestDetail = ({ r, kind, tx, onClose, photoToken, api, call }) => {
    const respMin = _durMin(r.created_at, r.acknowledged_at || r.responded_at);
    const resMin = _durMin(r.created_at, r.resolved_at);
    const Row = ({ label, value, testId }) => (
        <div className="flex justify-between gap-3 py-2 border-b border-white/5" data-testid={testId}>
            <span className="text-slate-400 text-xs shrink-0">{label}</span>
            <span className="text-slate-100 text-xs text-right">{value ?? tx.notFinished}</span>
        </div>
    );
    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 sm:p-4"
            data-testid="request-detail-panel" onClick={onClose}>
            <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-t-2xl sm:rounded-2xl max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-slate-900 flex items-center justify-between p-4 border-b border-white/10 z-10">
                    <p className="text-white font-semibold" data-testid="request-detail-title">{tx.requestDetail}</p>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="request-detail-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                        {r.urgent && <span className="px-2 py-0.5 rounded-full bg-rose-600 text-white text-[10px] font-bold">{tx.urgent}</span>}
                        <span className="text-white font-bold">{tx.room} {r.room_number}</span>
                        <span className={`ml-auto text-xs font-semibold ${r.status === 'resolved' ? 'text-emerald-300' : r.status === 'acknowledged' ? 'text-amber-300' : 'text-rose-300'}`}>
                            {tx.statusLabels[r.status] || r.status}</span>
                    </div>
                    {r.message && <p className="text-slate-100 text-sm mb-3 bg-white/5 border border-white/10 rounded-lg p-2.5">{r.message}</p>}
                    {Array.isArray(r.photo_ids) && r.photo_ids.length > 0 && api && (
                        <div className="flex flex-wrap gap-2 mb-3" data-testid="request-detail-photos">
                            {r.photo_ids.map((pid) => {
                                const src = `${api}/api/assistant/staff/request-photo/${pid}?s=${encodeURIComponent(photoToken || '')}`;
                                return (
                                    <a key={pid} href={src} target="_blank" rel="noreferrer"
                                        className="block w-20 h-20 rounded-lg overflow-hidden border border-white/10"
                                        data-testid={`request-photo-thumb-${pid}`}>
                                        <img src={src} alt="attachment" className="w-full h-full object-cover" />
                                    </a>
                                );
                            })}
                        </div>
                    )}
                    <Row label={tx.guest} value={r.guest_name || '—'} testId="rd-guest" />
                    {r.category && <Row label={tx.roomDetails ? tx.category || 'Category' : 'Category'} value={catLabel(tx, kind, r.category)} />}
                    <Row label={tx.createdLabel} value={fmtTime(r.created_at)} testId="rd-created" />
                    <Row label={tx.seenLabel} value={r.acknowledged_at ? `${fmtTime(r.acknowledged_at)}${r.assigned_staff_name ? ' · ' + r.assigned_staff_name : ''}` : tx.notFinished} testId="rd-seen" />
                    <Row label={tx.resolvedLabel} value={r.resolved_at ? `${fmtTime(r.resolved_at)}${r.assigned_staff_name ? ' · ' + r.assigned_staff_name : ''}` : tx.notFinished} testId="rd-resolved" />
                    <Row label={tx.responseTime} value={respMin != null ? `${respMin} ${tx.min}` : tx.notFinished} testId="rd-response" />
                    <Row label={tx.resolutionTime} value={resMin != null ? `${resMin} ${tx.min}` : tx.notFinished} testId="rd-resolution" />
                    {r.notes && <p className="text-slate-300 text-xs mt-3 italic" data-testid="rd-notes">{r.notes}</p>}
                    {call && (
                        <div className="mt-4 pt-3 border-t border-slate-700" onClick={(e) => e.stopPropagation()}>
                            <p className="text-xs font-semibold text-slate-400 mb-2">{tx.messages || 'Messages'}</p>
                            <RequestThread myRole="staff"
                                list={() => call(`requests/${kind}/${r.id}/messages`)}
                                send={(body) => call(`requests/${kind}/${r.id}/messages`, { method: 'POST', body: { body } })}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Staff-side ROUTING control — send work to the right department (or push it to
// management) from your own queue, on your staff session. The department list
// comes from the backend registry, never a hardcoded list per property.
const RouteRow = ({ tx, kind, reqId, call, onDone }) => {
    const [open, setOpen] = useState(false);
    const [depts, setDepts] = useState(null);
    const [target, setTarget] = useState('');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const openPanel = async () => {
        setOpen(true);
        setErr('');
        if (depts) return;
        try {
            const d = await call(`routing/departments?kind=${encodeURIComponent(kind)}`);
            setDepts(d.departments || []);
        } catch { setDepts([]); }
    };

    const send = async () => {
        if (!target) return;
        setBusy(true);
        setErr('');
        try {
            await call(`requests/${kind}/${reqId}/reassign`, {
                method: 'POST', body: { to_department: target, reason: reason.trim() || null },
            });
            setOpen(false);
            onDone && onDone();
        } catch { setErr(tx.routeFailed); }
        finally { setBusy(false); }
    };

    if (!open) {
        return (
            <button type="button" onClick={openPanel}
                className="w-full py-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-200 text-xs font-semibold transition-colors"
                data-testid={`staff-route-open-${reqId}`}>
                {tx.routeElsewhere}
            </button>
        );
    }
    return (
        <div className="rounded-lg bg-black/30 border border-white/10 p-3 space-y-2" data-testid={`staff-route-panel-${reqId}`}>
            <p className="text-slate-300 text-xs font-semibold">{tx.routeTitle}</p>
            <p className="text-slate-500 text-[11px]">{tx.routeHelp}</p>
            {!depts && <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />}
            <div className="flex flex-wrap gap-1.5">
                {(depts || []).map((d) => (
                    <button key={d.id} type="button" onClick={() => setTarget(d.id)}
                        className={`px-2.5 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${target === d.id ? 'bg-emerald-600 text-white' : d.is_management ? 'bg-violet-500/20 text-violet-200 hover:bg-violet-500/30' : 'bg-white/10 text-slate-300 hover:bg-white/20'}`}
                        data-testid={`staff-route-dept-${d.id}-${reqId}`}>
                        {tx.dept?.[d.id] || d.label}
                    </button>
                ))}
            </div>
            <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
                placeholder={tx.routeReasonPlaceholder}
                className="w-full rounded-lg bg-slate-900/60 border border-white/15 text-white text-xs p-2 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                data-testid={`staff-route-reason-${reqId}`} />
            {err && <p className="text-rose-400 text-[11px]" data-testid={`staff-route-error-${reqId}`}>{err}</p>}
            <div className="flex gap-2">
                <button type="button" onClick={() => setOpen(false)}
                    className="flex-1 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-200 text-xs font-semibold transition-colors"
                    data-testid={`staff-route-cancel-${reqId}`}>{tx.routeCancel}</button>
                <button type="button" onClick={send} disabled={busy || !target}
                    className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                    data-testid={`staff-route-confirm-${reqId}`}>
                    {busy ? '…' : tx.routeConfirm}
                </button>
            </div>
        </div>
    );
};

export const QueueView = ({ tx, kind, call, onBack, photoToken, api, titleOverride }) => {
    const [tab, setTab] = useState('open');
    const [items, setItems] = useState(null);
    const [notes, setNotes] = useState({});
    const [busy, setBusy] = useState('');
    const [detail, setDetail] = useState(null);
    // Visible-history dismissal — hides resolved rows from THIS device only; the
    // backend record is always retained for managers.
    const dkey = `hotelops_dismissed_${kind}`;
    const [dismissed, setDismissed] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem(dkey) || '[]')); } catch { return new Set(); }
    });
    const [clearedMsg, setClearedMsg] = useState('');

    const load = useCallback(async (t) => {
        try {
            const qs = t === 'mine' ? 'status=open&mine=true' : `status=${t === 'open' ? 'open' : 'resolved'}`;
            const d = await call(`requests/${kind}?${qs}`);
            setItems(d.requests || []);
        } catch { /* locked handled upstream */ }
    }, [call, kind]);
    useEffect(() => { setItems(null); setClearedMsg(''); load(tab); }, [tab, load]);

    const act = async (id, action) => {
        setBusy(id + action);
        try {
            await call(`requests/${kind}/${id}/action`, {
                method: 'POST', body: { action, notes: (notes[id] || '').trim() || null },
            });
            await load(tab);
        } catch { /* locked handled upstream */ }
        finally { setBusy(''); }
    };

    const clearHistory = () => {
        const next = new Set(dismissed);
        (items || []).forEach((r) => next.add(r.id));
        setDismissed(next);
        try { localStorage.setItem(dkey, JSON.stringify([...next])); } catch {}
        setClearedMsg(tx.historyCleared);
    };

    const visible = (items || []).filter((r) => tab !== 'resolved' || !dismissed.has(r.id));
    const title = titleOverride || (kind === 'security' ? tx.security : tx.concierge);
    return (
        <Shell title={title} tx={tx} onBack={onBack} onRefresh={() => load(tab)} testId={`staff-${kind}-view`}>
            <div className="flex gap-2 mb-4 items-center">
                {['mine', 'open', 'resolved'].map((t) => (
                    <button key={t} type="button" onClick={() => setTab(t)}
                        className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${tab === t ? 'bg-emerald-600 text-white' : 'bg-white/10 text-slate-300 hover:bg-white/20'}`}
                        data-testid={`staff-${kind}-tab-${t}`}>
                        {t === 'mine' ? (tx.assignedToMe || 'Assigned to me') : t === 'open' ? tx.open : tx.resolvedTab}
                    </button>
                ))}
                {tab === 'resolved' && visible.length > 0 && (
                    <button type="button" onClick={clearHistory}
                        className="ml-auto text-xs text-slate-400 hover:text-white transition-colors"
                        data-testid={`staff-${kind}-clear-history`}>{tx.clearHistory}</button>
                )}
            </div>
            {tx.queueHelp && <p className="text-slate-500 text-xs mb-3" data-testid={`staff-${kind}-help`}>{tx.queueHelp}</p>}
            {!items && <Loader2 className="w-6 h-6 text-emerald-400 animate-spin mx-auto" />}
            {clearedMsg && <p className="text-slate-400 text-xs mb-3" data-testid={`staff-${kind}-cleared-msg`}>{clearedMsg}</p>}
            {items && visible.length === 0 && !clearedMsg && <p className="text-slate-400 text-sm" data-testid={`staff-${kind}-empty`}>{tx.noRequests}</p>}
            <div className="space-y-3">
                {visible.map((r) => (
                    <div key={r.id} className={`rounded-xl p-4 border ${r.urgent && r.status !== 'resolved' ? 'bg-rose-950/50 border-rose-500/40' : 'bg-white/5 border-white/10'}`}
                        data-testid={`staff-request-${r.id}`}>
                        <button type="button" onClick={() => setDetail(r)} className="w-full text-left" data-testid={`staff-request-open-${r.id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                                {r.urgent && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-600 text-white text-[10px] font-bold">
                                        <AlertTriangle className="w-3 h-3" /> {tx.urgent}
                                    </span>
                                )}
                                <span className="text-white font-semibold text-sm">{tx.room} {r.room_number}</span>
                                {r.guest_name && (
                                    <span className="text-slate-300 text-xs" data-testid={`staff-request-guest-${r.id}`}>· {r.guest_name}</span>
                                )}
                                <span className="text-slate-500 text-xs ml-auto flex items-center gap-1"><Clock className="w-3 h-3" />{fmtTime(r.created_at)}</span>
                            </div>
                            {r.category && <p className="text-slate-300 text-xs mt-2 capitalize">{catLabel(tx, kind, r.category)}</p>}
                            {r.message && <p className="text-slate-200 text-sm mt-1">{r.message}</p>}
                            <p className="text-xs mt-2 text-slate-400">
                                {tx.status}: <span className={r.status === 'resolved' ? 'text-emerald-300' : r.status === 'acknowledged' ? 'text-amber-300' : 'text-rose-300'}>
                                    {tx.statusLabels[r.status] || r.status}</span>
                                {r.assigned_staff_name && <> · {tx.handledBy} <span className="text-slate-200">{r.assigned_staff_name}</span></>}
                            </p>
                            <p className="text-[10px] text-slate-500 mt-1">{tx.tapForDetails}</p>
                        </button>
                        {r.status !== 'resolved' && (
                            <div className="mt-3 space-y-2">
                                <input value={notes[r.id] || ''} onChange={(e) => setNotes((p) => ({ ...p, [r.id]: e.target.value }))}
                                    placeholder={tx.notesPlaceholder} maxLength={500}
                                    className="w-full rounded-lg bg-slate-900/60 border border-white/15 text-white text-xs p-2 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                                    data-testid={`staff-notes-${r.id}`} />
                                <div className="flex gap-2">
                                    {r.status === 'open' && (
                                        <button type="button" onClick={() => act(r.id, 'acknowledge')} disabled={!!busy}
                                            className="flex-1 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors disabled:opacity-60"
                                            data-testid={`staff-ack-${r.id}`}>
                                            {busy === r.id + 'acknowledge' ? '…' : tx.acknowledge}
                                        </button>
                                    )}
                                    <button type="button" onClick={() => act(r.id, 'resolve')} disabled={!!busy}
                                        className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors disabled:opacity-60"
                                        data-testid={`staff-resolve-${r.id}`}>
                                        {busy === r.id + 'resolve' ? '…' : tx.resolve}
                                    </button>
                                </div>
                                <RouteRow tx={tx} kind={kind} reqId={r.id} call={call} onDone={() => load(tab)} />
                            </div>
                        )}
                        {r.status === 'resolved' && <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-2" />}
                    </div>
                ))}
            </div>
            {detail && <RequestDetail r={detail} kind={kind} tx={tx} onClose={() => setDetail(null)} photoToken={photoToken} api={api} call={call} />}
        </Shell>
    );
};

// Valet queue — RETRIEVAL REQUESTED → RETRIEVING → READY → DELIVERED.
// Ticket number is the vehicle identifier; room is an association.
export const ValetView = ({ tx, call, onBack }) => {
    const [reqs, setReqs] = useState(null);
    const [busyId, setBusyId] = useState('');
    const [lookup, setLookup] = useState('');
    const [found, setFound] = useState(null);
    const [lookupErr, setLookupErr] = useState('');

    const load = useCallback(async () => {
        try { const d = await call('valet/requests?status=open'); setReqs(d.requests || []); }
        catch { /* locked handled upstream */ }
    }, [call]);
    useEffect(() => { load(); }, [load]);

    const advance = async (id, status) => {
        setBusyId(id);
        try { await call(`valet/${id}/status`, { method: 'POST', body: { status } }); await load(); }
        catch { /* locked */ }
        finally { setBusyId(''); }
    };

    const doLookup = async () => {
        const t = lookup.replace('#', '').trim();
        if (!t) return;
        setLookupErr('');
        setFound(null);
        try { setFound(await call(`valet/lookup/${encodeURIComponent(t)}`)); }
        catch { setLookupErr(tx.valetNotFound); }
    };

    const NEXT = { retrieval_requested: ['retrieving'], retrieving: ['ready'], ready: ['delivered'] };
    const actionLabel = { retrieving: tx.valetStartRetrieving, ready: tx.valetMarkReady, delivered: tx.valetMarkDelivered };

    const RecordCard = ({ r }) => (
        <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`staff-valet-${r.ticket}`}>
            <div className="flex items-center justify-between gap-2">
                <p className="text-white font-bold text-sm">#{r.ticket}</p>
                <span className="text-[11px] font-semibold text-amber-300 uppercase tracking-wide">{tx[`valet_${r.status}`] || r.status}</span>
            </div>
            <p className="text-slate-200 text-sm mt-1.5">
                {tx.room} {r.room_number} — {r.guest_name}
            </p>
            <p className="text-slate-300 text-sm">
                {[r.vehicle?.color, r.vehicle?.make, r.vehicle?.model].filter(Boolean).join(' ')}
                {r.vehicle?.plate ? ` · ${tx.plate} ${r.vehicle.plate}` : ''}
            </p>
            <p className="text-slate-400 text-xs mt-1 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> {tx.valetRequestedFor}: {r.requested_for}
            </p>
            {r.attendant_name && <p className="text-slate-500 text-xs mt-0.5">{tx.valetAttendant}: {r.attendant_name}</p>}
            <div className="flex gap-2 mt-3">
                {(NEXT[r.status] || []).map((s) => (
                    <button key={s} type="button" onClick={() => advance(r.id, s)} disabled={busyId === r.id}
                        className="flex-1 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors disabled:opacity-50"
                        data-testid={`staff-valet-${s}-${r.ticket}`}>
                        {busyId === r.id ? '…' : actionLabel[s]}
                    </button>
                ))}
                {r.status !== 'delivered' && r.status !== 'cancelled' && (
                    <button type="button" onClick={() => advance(r.id, 'cancelled')} disabled={busyId === r.id}
                        className="px-3 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-slate-300 text-sm transition-colors disabled:opacity-50"
                        data-testid={`staff-valet-cancel-${r.ticket}`}>
                        {tx.valetCancel}
                    </button>
                )}
            </div>
        </div>
    );

    if (!reqs) return <Shell title={tx.valet} tx={tx} onBack={onBack} testId="staff-valet-view"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin mx-auto" /></Shell>;

    return (
        <Shell title={tx.valet} tx={tx} onBack={onBack} onRefresh={load} testId="staff-valet-view">
            <div className="flex gap-2 mb-4">
                <input value={lookup} onChange={(e) => setLookup(e.target.value)} placeholder={tx.valetLookupPh}
                    className="flex-1 rounded-xl bg-slate-900/60 border border-white/15 text-white text-sm p-2.5 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                    data-testid="staff-valet-lookup-input" />
                <button type="button" onClick={doLookup}
                    className="px-4 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
                    data-testid="staff-valet-lookup-btn">
                    {tx.valetLookup}
                </button>
            </div>
            {lookupErr && <p className="text-rose-400 text-xs mb-3" data-testid="staff-valet-lookup-error">{lookupErr}</p>}
            {found && (
                <div className="mb-4" data-testid="staff-valet-lookup-result">
                    <RecordCard r={found} />
                    <button type="button" onClick={() => { setFound(null); setLookup(''); }}
                        className="mt-2 text-xs text-slate-400 hover:text-white" data-testid="staff-valet-lookup-clear">
                        {tx.valetClearLookup}
                    </button>
                </div>
            )}
            {reqs.length === 0 && !found && <p className="text-slate-400 text-sm" data-testid="staff-valet-empty">{tx.valetEmpty}</p>}
            <div className="space-y-3">
                {reqs.map((r) => <RecordCard key={r.id} r={r} />)}
            </div>
        </Shell>
    );
};

export const ManagerView = ({ tx, call, onBack, onOpenSector, isHotel = true }) => {
    const [data, setData] = useState(null);
    const load = useCallback(async () => {
        try { setData(await call('ops-summary')); } catch { /* locked handled upstream */ }
    }, [call]);
    useEffect(() => { load(); }, [load]);

    if (!data) return <Shell title={tx.management} tx={tx} onBack={onBack} testId="staff-manager-view"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin mx-auto" /></Shell>;

    const Stat = ({ label, value, testId }) => (
        <div className="bg-slate-900/50 rounded-lg p-2.5" data-testid={testId}>
            <p className="text-slate-400 text-[10px] uppercase tracking-wide">{label}</p>
            <p className="text-white font-bold text-lg">{value ?? '—'}</p>
        </div>
    );

    // Live per-sector notification counts (open items) — real data, not hard-coded.
    // Housekeeping and valet are hotel-only concepts; hide them on non-hotel
    // properties so a condo manager never sees irrelevant empty sectors.
    const sectors = [
        isHotel && { key: 'housekeeping', label: tx.housekeeping, count: data.housekeeping?.open || 0 },
        { key: 'security', label: tx.security, count: data.security?.open || 0 },
        { key: 'concierge', label: tx.concierge, count: data.concierge?.open || 0 },
        isHotel && { key: 'valet', label: tx.valet, count: data.valet?.open || 0 },
        { key: 'management', label: tx.management, count: data.management?.open || 0, noOpen: true },
    ].filter(Boolean);

    return (
        <Shell title={tx.summaryTitle} tx={tx} onBack={onBack} onRefresh={load} testId="staff-manager-view">
            <div className="space-y-2 mb-5" data-testid="mgr-sector-overview">
                {sectors.map((s) => (
                    <div key={s.key} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3"
                        data-testid={`mgr-sector-${s.key}`}>
                        <span className="text-white font-semibold text-sm flex-1">{s.label}</span>
                        <span className={`inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full text-xs font-bold ${s.count > 0 ? 'bg-emerald-600 text-white' : 'bg-white/10 text-slate-400'}`}
                            data-testid={`mgr-count-${s.key}`}>{s.count}</span>
                        <span className="text-slate-400 text-[11px]">{tx.notifSuffix}</span>
                        {onOpenSector && !s.noOpen && (
                            <button type="button" onClick={() => onOpenSector(s.key)}
                                className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
                                data-testid={`mgr-open-${s.key}`}>{tx.sectorOpenBtn}</button>
                        )}
                    </div>
                ))}
            </div>
            {['security', 'concierge', 'valet'].filter((k) => isHotel || k !== 'valet').map((k) => (
                <div key={k} className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4" data-testid={`mgr-${k}-card`}>
                    <p className="text-white font-semibold text-sm mb-3">{k === 'security' ? tx.security : k === 'concierge' ? tx.concierge : tx.valet}</p>
                    <div className="grid grid-cols-3 gap-2">
                        <Stat label={tx.openNow} value={data[k]?.open} testId={`mgr-${k}-open`} />
                        {k === 'security' && <Stat label={tx.urgentOpen} value={data[k]?.urgent_open} testId={`mgr-${k}-urgent`} />}
                        <Stat label={tx.resolved7d} value={data[k]?.resolved_7d} testId={`mgr-${k}-resolved`} />
                        <Stat label={`${tx.avgResponse} (${tx.min})`} value={data[k]?.avg_response_min} testId={`mgr-${k}-response`} />
                        <Stat label={`${tx.avgResolution} (${tx.min})`} value={data[k]?.avg_resolution_min} testId={`mgr-${k}-resolution`} />
                    </div>
                </div>
            ))}
            {isHotel && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="mgr-housekeeping-card">
                <p className="text-white font-semibold text-sm mb-3">{tx.housekeeping}</p>
                <div className="grid grid-cols-3 gap-2">
                    <Stat label={tx.dndRooms} value={data.housekeeping?.dnd_rooms} testId="mgr-hk-dnd" />
                    <Stat label={tx.readyRooms} value={data.housekeeping?.ready_rooms} testId="mgr-hk-ready" />
                    <Stat label={tx.trackedRooms} value={data.housekeeping?.tracked_rooms} testId="mgr-hk-tracked" />
                </div>
            </div>
            )}
        </Shell>
    );
};


// ─── Hotel Ops (C3) — role-aware Room Details panel ──────────────────
const _DetailSection = ({ title, testId, children }) => (
    <div className="mb-3" data-testid={testId}>
        <p className="text-white text-sm font-semibold mb-1.5">{title}</p>
        {children}
    </div>
);

const _ActivityRow = ({ r, tx }) => (
    <div className="bg-white/5 border border-white/10 rounded-lg p-2.5 mt-1.5">
        <div className="flex items-center gap-2">
            {r.urgent && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-600 text-white text-[9px] font-bold">
                    <AlertTriangle className="w-2.5 h-2.5" /> {tx.urgent}
                </span>
            )}
            <span className="text-white text-xs font-semibold capitalize">{(r.category || '').replace(/_/g, ' ') || '—'}</span>
            <span className={`ml-auto text-[11px] ${r.status === 'resolved' ? 'text-emerald-300' : r.status === 'acknowledged' ? 'text-amber-300' : 'text-rose-300'}`}>
                {(tx.statusLabels && tx.statusLabels[r.status]) || r.status}
            </span>
        </div>
        {r.message && <p className="text-slate-300 text-xs mt-1">{r.message}</p>}
        <p className="text-slate-500 text-[11px] mt-1">
            {fmtTime(r.created_at)}{r.assigned_staff_name ? ` · ${tx.handledBy} ${r.assigned_staff_name}` : ''}
        </p>
    </div>
);

export const RoomDetailsPanel = ({ roomNumber, callSafe, tx, onClose }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { ok, data: d } = await callSafe(`room-details/${encodeURIComponent(roomNumber)}`);
                if (!alive) return;
                if (ok) setData(d); else setError(d?.detail === 'room_not_found' ? tx.roomNotFound : tx.error);
            } catch { if (alive) setError(tx.networkError); }
        })();
        return () => { alive = false; };
    }, [roomNumber, callSafe, tx]);

    const stateColor = { red: 'text-rose-400', orange: 'text-amber-400', green: 'text-emerald-400' };
    const stateDot = { red: 'bg-rose-500', orange: 'bg-amber-500', green: 'bg-emerald-500' };
    const stateLabel = { red: tx.stateRed, orange: tx.stateOrange, green: tx.stateGreen };
    const v = data?.visible || [];
    const hk = data?.sections?.housekeeping || {};

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 sm:p-4"
            data-testid="room-details-panel" onClick={onClose}>
            <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-t-2xl sm:rounded-2xl max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-slate-900 flex items-center justify-between p-4 border-b border-white/10 z-10">
                    <p className="text-white font-semibold" data-testid="room-details-title">{tx.roomDetails}</p>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-white"
                        data-testid="room-details-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-4">
                    {!data && !error && <Loader2 className="w-6 h-6 text-emerald-400 animate-spin mx-auto" />}
                    {error && <p className="text-rose-400 text-sm text-center" data-testid="room-details-error">{error}</p>}
                    {data && (
                        <>
                            <div className="flex items-center gap-3 mb-4">
                                <DoorClosed className={`w-9 h-9 ${stateColor[data.state]}`} strokeWidth={1.75} />
                                <div>
                                    <p className="text-white text-xl font-bold">{tx.room} {data.room_number}</p>
                                    <p className="text-xs text-slate-400">{tx.floor} {data.floor}</p>
                                </div>
                                <span className={`ml-auto inline-flex items-center gap-1.5 text-sm font-semibold ${stateColor[data.state]}`}
                                    data-testid="room-details-state">
                                    <span className={`w-2.5 h-2.5 rounded-full ${stateDot[data.state]}`} />{stateLabel[data.state]}
                                </span>
                            </div>

                            <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-3" data-testid="room-details-guest">
                                <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">{tx.guest}</p>
                                {data.guest ? (
                                    <>
                                        <p className="text-white text-sm font-semibold">{data.guest.name || '—'}</p>
                                        <p className="text-slate-400 text-xs mt-0.5">{tx.checkOut}: {fmtTime(data.guest.checkout_at)}</p>
                                    </>
                                ) : <p className="text-slate-400 text-sm">{tx.vacant}</p>}
                            </div>

                            {v.includes('housekeeping') && (
                                <_DetailSection title={tx.housekeeping} testId="room-details-housekeeping">
                                    {hk.last_cleaned_at
                                        ? <p className="text-slate-300 text-xs">{tx.lastCleanedBy} {hk.last_cleaned_by || '—'} · {fmtTime(hk.last_cleaned_at)}</p>
                                        : <p className="text-slate-400 text-xs">{tx.noActivity}</p>}
                                    {data.dnd && <p className="text-amber-300 text-xs mt-1">{tx.stateOrange} · {hk.dnd_until || '—'}</p>}
                                    {(hk.history || []).map((h, i) => (
                                        <p key={i} className="text-slate-400 text-xs mt-1" data-testid="room-details-hk-history">
                                            {(stateLabel[h.new_state] || h.event)}{h.staff_name ? ` · ${h.staff_name}` : (h.by === 'guest' ? ` · ${tx.guest}` : '')} · {fmtTime(h.at)}{h.reason ? ` — ${h.reason}` : ''}
                                        </p>
                                    ))}
                                </_DetailSection>
                            )}

                            {v.includes('security') && (
                                <_DetailSection title={tx.security} testId="room-details-security">
                                    {(data.sections.security || []).length === 0
                                        ? <p className="text-slate-400 text-xs">{tx.noRequests}</p>
                                        : data.sections.security.map((r) => <_ActivityRow key={r.id} r={r} tx={tx} />)}
                                </_DetailSection>
                            )}

                            {v.includes('concierge') && (
                                <_DetailSection title={tx.concierge} testId="room-details-concierge">
                                    {(data.sections.concierge || []).length === 0
                                        ? <p className="text-slate-400 text-xs">{tx.noRequests}</p>
                                        : data.sections.concierge.map((r) => <_ActivityRow key={r.id} r={r} tx={tx} />)}
                                </_DetailSection>
                            )}

                            {v.includes('valet') && (
                                <_DetailSection title={tx.valet} testId="room-details-valet">
                                    {(data.sections.valet || []).length === 0
                                        ? <p className="text-slate-400 text-xs">{tx.valetEmpty}</p>
                                        : data.sections.valet.map((r) => (
                                            <div key={r.id} className="bg-white/5 border border-white/10 rounded-lg p-2.5 mt-1.5 text-slate-300 text-xs">
                                                <span className="text-white font-semibold">#{r.ticket}</span> · {[r.vehicle?.color, r.vehicle?.make, r.vehicle?.model].filter(Boolean).join(' ')} · <span className="text-amber-300">{tx[`valet_${r.status}`] || r.status}</span>
                                            </div>
                                        ))}
                                </_DetailSection>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
